﻿import { useMemo } from 'react';
import { Grid, useTheme, alpha } from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Cell,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import { Mail as MailIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import type { MetricsData } from '@/types';
import { MetricsChartCard, MetricsSectionHeading, makeTooltipStyle } from './index';

interface Props {
    data: MetricsData;
}

export const MetricsCommunicationSection = ({ data }: Props) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const tt = makeTooltipStyle(theme);
    const divider = alpha(theme.palette.divider, 0.5);
    const tickStyle = { fill: theme.palette.text.secondary, fontSize: 12 };
    const isFiltered = data.resourceId !== null;
    const filteredLabel = LL.metricsPage.filteredByUser();

    const mailsPerDay = useMemo(
        () =>
            (data.mails.perDay ?? []).map(({ day, reminders, notifications }) => ({
                day: new Date(day).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                }),
                reminders,
                notifications,
            })),
        [data.mails.perDay]
    );

    const changeRequestsPerDay = useMemo(
        () =>
            (data.changeRequests.perDay ?? []).map(({ day, created, cancelled, accepted }) => ({
                day: new Date(day).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                }),
                created,
                cancelled,
                accepted,
            })),
        [data.changeRequests.perDay]
    );

    const categoryRequestsPerDay = useMemo(
        () =>
            (data.categoryRequests.perDay ?? []).map(({ day, accepted, denied, pending }) => ({
                day: new Date(day).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                }),
                accepted,
                denied,
                pending,
            })),
        [data.categoryRequests.perDay]
    );

    const reminderIntervalData = (data.mails.reminderIntervals ?? []).map(({ days, count }) => ({
        name: LL.metricsPage.charts.reminderIntervalLabel({ days }),
        count,
    }));
    // "No reminders" = total active users minus those with any reminder enabled
    const totalActiveUsers = data.users.active;
    const usersWithReminder = data.mails.remindersConfigured;
    reminderIntervalData.push({
        name: LL.metricsPage.charts.noReminders(),
        count: Math.max(0, totalActiveUsers - usersWithReminder),
    });

    const hasMailActivity = mailsPerDay.length > 0;
    const hasCrActivity = changeRequestsPerDay.length > 0;
    const hasCatReqActivity = categoryRequestsPerDay.length > 0;

    return (
        <MetricsSectionHeading
            icon={<MailIcon fontSize="small" />}
            title={LL.metricsPage.sections.communication()}
        >
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                {/* ── Mails per day line chart ── */}
                {hasMailActivity && (
                    <Grid size={{ xs: 12, md: 8 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.mailsPerDay()}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={mailsPerDay}
                                    margin={{ left: 0, right: 16, top: 8, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis
                                        dataKey="day"
                                        tick={tickStyle}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis allowDecimals={false} tick={tickStyle} />
                                    <Tooltip {...tt} />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="reminders"
                                        stroke={theme.palette.primary.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.charts.mailsRemindersLabel()}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="notifications"
                                        stroke={theme.palette.secondary.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.charts.mailsNotificationsLabel()}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* ── Reminder intervals bar chart ── */}
                <Grid size={{ xs: 12, md: hasMailActivity ? 4 : 6 }}>
                    <MetricsChartCard title={LL.metricsPage.charts.reminderIntervals()}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={reminderIntervalData}
                                margin={{ left: 0, right: 16, top: 8 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                <XAxis
                                    dataKey="name"
                                    tick={{ ...tickStyle, fontSize: 11 }}
                                    interval={0}
                                />
                                <YAxis allowDecimals={false} tick={tickStyle} />
                                <Tooltip {...tt} />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                    {reminderIntervalData.map((_, i) => (
                                        <Cell
                                            key={i}
                                            fill={
                                                i < reminderIntervalData.length - 1
                                                    ? theme.palette.primary.main
                                                    : theme.palette.error.main
                                            }
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </MetricsChartCard>
                </Grid>

                {/* ── Change requests per day line chart ── */}
                {hasCrActivity && (
                    <Grid size={{ xs: 12 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.changeRequestsPerDay()}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={changeRequestsPerDay}
                                    margin={{ left: 0, right: 16, top: 8, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis
                                        dataKey="day"
                                        tick={tickStyle}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis allowDecimals={false} tick={tickStyle} />
                                    <Tooltip {...tt} />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="created"
                                        stroke={theme.palette.warning.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.charts.changeRequestsCreatedLabel()}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="cancelled"
                                        stroke={theme.palette.error.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.charts.changeRequestsCancelledLabel()}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="accepted"
                                        stroke={theme.palette.success.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.charts.changeRequestsAcceptedLabel()}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* ── Category requests per day line chart ── */}
                {hasCatReqActivity && (
                    <Grid size={{ xs: 12 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.categoryRequestsPerDay()}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={categoryRequestsPerDay}
                                    margin={{ left: 0, right: 16, top: 8, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis
                                        dataKey="day"
                                        tick={tickStyle}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis allowDecimals={false} tick={tickStyle} />
                                    <Tooltip {...tt} />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="accepted"
                                        stroke={theme.palette.success.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.charts.categoryRequestsAcceptedLabel()}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="denied"
                                        stroke={theme.palette.error.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.charts.categoryRequestsDeniedLabel()}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="pending"
                                        stroke={theme.palette.warning.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.charts.categoryRequestsPendingLabel()}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* ── Absences over time ── */}
                {data.absences.perDate.length > 0 && (
                    <Grid size={{ xs: 12 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.absencesOverTime()}
                            height={220}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={data.absences.perDate}
                                    margin={{ left: 0, right: 16, top: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ ...tickStyle, fontSize: 10 }}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis allowDecimals={false} tick={tickStyle} />
                                    <Tooltip {...tt} />
                                    <Bar
                                        dataKey="count"
                                        fill={theme.palette.error.main}
                                        radius={[3, 3, 0, 0]}
                                        name={LL.metricsPage.stats.absencesTotal()}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}
            </Grid>
        </MetricsSectionHeading>
    );
};
